import RepairFileService from '@/services/RepairFileService'
import { refreshUploadResult } from './uploadResultUtils'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg'
const PROOF_FIELDS = {
  PURCHASE_PROOF: 'product_buy_proof_file_path',
  GUARANTEE_EXTENSION_PROOF: 'product_guarantee_extension_proof_file_path',
  FORCE_SUPPORT_PROOF: 'product_force_support_proof_file_path',
  ASBESTOS_PROOF: 'product_asbestos_proof_file_path',
  REPAIRER_ESTIMATE: 'repairer_estimate_file_path'
}

export function createRepairAdditionalUploadAdapter(repairFileId) {
  const readCurrent = async () => {
    const snapshot = await RepairFileService.getAdditionalAttachmentFileList(repairFileId)
    return {
      snapshot,
      attachments: (snapshot.item_list ?? []).map((item) => ({
        id: item.technical_id,
        name: item.name
      }))
    }
  }
  return {
    metadata: { visibleToKeyAccount: true },
    capabilities: {
      multiple: false,
      accept: ACCEPT,
      requiresName: true,
      maxFileNameLength: 255,
      supportsQr: true,
      supportsDelete: false
    },
    readCurrent,
    async uploadLocal({ files, fileName, metadata }) {
      const data = new FormData()
      data.append(
        'data',
        JSON.stringify({
          repair_file_id: repairFileId,
          file_name: fileName,
          visible_to_key_account: metadata.visibleToKeyAccount
        })
      )
      data.append('additional_attachment', files[0])
      await RepairFileService.uploadAdditionalAttachmentFile(data)
      return refreshUploadResult(readCurrent)
    },
    async createQrSession({ metadata }) {
      const { token } = await RepairFileService.getUploadAdditionalAttachmentQRCodeToken(
        repairFileId
      )
      const params = new URLSearchParams({
        token,
        visibleToKeyAccount: String(metadata.visibleToKeyAccount)
      })
      return {
        token,
        url: `${window.location.origin}/public/repair-file/upload-additional-attachment?${params}`
      }
    },
    async verifyQrUpload({ session }) {
      const receipt =
        await RepairFileService.validateRepairFileUploadAdditionalAttachmentQrCodeToken(
          session.token
        )
      const completed = receipt.upload_completed === true
      return completed ? refreshUploadResult(readCurrent, { completed }) : { completed }
    }
  }
}

export function createRepairProofUploadAdapter(repairFile, attachmentType) {
  const field = PROOF_FIELDS[attachmentType]
  if (!field) throw new Error('ERROR.INVALID_ATTACHMENT_PROOF_TYPE')
  const readCurrent = async () => {
    const snapshot = await RepairFileService.get(repairFile.boomerang_id)
    return {
      snapshot,
      attachments: snapshot[field]
        ? [
            {
              id: attachmentType,
              name:
                attachmentType === 'REPAIRER_ESTIMATE'
                  ? snapshot.repairer_estimate_filename || snapshot[field]
                  : snapshot[field]
            }
          ]
        : []
    }
  }
  return {
    capabilities: {
      multiple: false,
      accept: ACCEPT,
      maxTotal: 1,
      requiresName: false,
      maxFileNameLength: 255,
      supportsQr: true,
      supportsDelete: true,
      deleteBeforeUpload: true
    },
    readCurrent,
    async uploadLocal({ files }) {
      const data = new FormData()
      data.append('data', JSON.stringify({ repairFileId: repairFile.technical_id, attachmentType }))
      data.append('attachmentFile', files[0])
      await RepairFileService.addAttachmentFile(data)
      return refreshUploadResult(readCurrent)
    },
    async createQrSession() {
      const { token } = await RepairFileService.getAddAttachmentQRCodeToken(
        repairFile.technical_id,
        attachmentType
      )
      return {
        token,
        url: `${
          window.location.origin
        }/public/repair-file/add-attachment?token=${encodeURIComponent(token)}`
      }
    },
    async verifyQrUpload({ session }) {
      const receipt = await RepairFileService.validateRepairFileAddAttachmentQrCodeToken(
        session.token
      )
      const completed = receipt.upload_completed === true
      return completed ? refreshUploadResult(readCurrent, { completed }) : { completed }
    },
    async finalizeQrSession({ session }) {
      await RepairFileService.confirmAttachmentAddedByQrCode(repairFile.technical_id, session.token)
      return refreshUploadResult(readCurrent)
    },
    async removeExisting() {
      await RepairFileService.removeAttachmentFromRepairFile(
        repairFile.technical_id,
        attachmentType
      )
      return refreshUploadResult(readCurrent)
    }
  }
}
